import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  Paper,
  CircularProgress,
  Typography,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useResponsive } from '../hooks/useResponsive';

const DataTable = ({
  columns,
  data,
  loading,
  searchable,
  searchPlaceholder,
  emptyMessage,
  defaultRowsPerPage,
  rowsPerPageOptions,
  onRowClick,
  stickyHeader,
}) => {
  const { isMobile } = useResponsive();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState({});

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(0);
  };

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  const filteredData = useMemo(() => {
    if (!searchQuery) return data;

    const lowerQuery = searchQuery.toLowerCase();
    return data.filter((row) =>
      columns.some((column) => {
        if (!column.searchable) return false;
        const value = getNestedValue(row, column.field);
        return value?.toString().toLowerCase().includes(lowerQuery);
      })
    );
  }, [data, searchQuery, columns]);

  const sortedData = useMemo(() => {
    if (!orderBy) return filteredData;

    const column = columns.find((col) => col.field === orderBy);
    if (!column || !column.sortable) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = getNestedValue(a, orderBy);
      const bValue = getNestedValue(b, orderBy);

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'string') {
        return order === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return order === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [filteredData, orderBy, order, columns]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const renderCellContent = (row, column) => {
    if (column.render) {
      return column.render(row);
    }
    const value = getNestedValue(row, column.field);
    return value ?? '-';
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const renderMobileView = () => {
    return (
      <Box>
        {paginatedData.map((row, index) => {
          const rowId = row.id || index;
          const isExpanded = expandedRows[rowId];
          const primaryColumn = columns.find(col => col.primary) || columns[0];
          const secondaryColumn = columns.find(col => col.secondary);

          return (
            <Card
              key={rowId}
              sx={{
                mb: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  onClick={() => onRowClick && onRowClick(row)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {renderCellContent(row, primaryColumn)}
                    </Typography>
                    {secondaryColumn && (
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {renderCellContent(row, secondaryColumn)}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); toggleRowExpansion(rowId); }}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={1}>
                    {columns
                      .filter(col => !col.primary && !col.secondary)
                      .map((col) => (
                        <Box key={col.field} display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            {col.headerName}:
                          </Typography>
                          <Typography variant="body2" fontWeight="500">
                            {renderCellContent(row, col)}
                          </Typography>
                        </Box>
                      ))}
                  </Stack>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {searchable && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {data.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Paper>
      ) : filteredData.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No results found for "{searchQuery}"
          </Typography>
        </Paper>
      ) : (
        <>
          {isMobile ? (
            renderMobileView()
          ) : (
            <TableContainer component={Paper}>
              <Table stickyHeader={stickyHeader}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        sx={{
                          fontWeight: 600,
                          backgroundColor: 'grey.50',
                          minWidth: column.minWidth,
                          width: column.width,
                        }}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.field}
                            direction={orderBy === column.field ? order : 'asc'}
                            onClick={() => handleRequestSort(column.field)}
                          >
                            {column.headerName}
                          </TableSortLabel>
                        ) : (
                          column.headerName
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row, index) => (
                    <TableRow
                      key={row.id || index}
                      hover
                      onClick={() => onRowClick && onRowClick(row)}
                      sx={{
                        cursor: onRowClick ? 'pointer' : 'default',
                        '&:last-child td, &:last-child th': { border: 0 },
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.field}
                          align={column.align || 'left'}
                        >
                          {renderCellContent(row, column)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: isMobile ? 2 : 0 }}
          />
        </>
      )}
    </Box>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      headerName: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      searchable: PropTypes.bool,
      align: PropTypes.oneOf(['left', 'center', 'right']),
      minWidth: PropTypes.number,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      render: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  emptyMessage: PropTypes.string,
  defaultRowsPerPage: PropTypes.number,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  onRowClick: PropTypes.func,
  stickyHeader: PropTypes.bool,
};

DataTable.defaultProps = {
  loading: false,
  searchable: true,
  searchPlaceholder: 'Search...',
  emptyMessage: 'No data available',
  defaultRowsPerPage: 10,
  rowsPerPageOptions: [10, 25, 50],
  onRowClick: null,
  stickyHeader: false,
};

export default DataTable;
